"use client"

// src/components/TablaGastosComponent.js
import { useState, useEffect, useContext } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  Autocomplete,
  TextField,
  InputAdornment,
  Modal,
  Box,
  Typography,
  Button,
  alpha,
  Paper,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material"
import PersonIcon from "@mui/icons-material/Person"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import SearchIcon from "@mui/icons-material/Search"

import { ErpContext } from "../../context/ErpContext"
import CustomPagination from "./CustomPagination"
import GraficasGastos from "./graficas/GraficasGastos"
import GestionGastosComponent from "./GestionGastosComponent"

import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from "dayjs"

import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UndoIcon from '@mui/icons-material/Undo';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { ThemeProvider, createTheme } from "@mui/material/styles"

// Reutilizo los estilos de la tabla de ventas (colores de header, hover, etc.)
import "./TablaVentasComponent.css"
import { reservePdfTarget } from "../../context/pdf_esqueletos/helpers/pdfDelivery.js"

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#5b5b5f" },
    secondary: { main: "rgb(244, 177, 14)" },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": { boxShadow: "0 4px 8px rgba(0,0,0,0.1)" },
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 500 } },
    },
  },
})

export default function TablaGastosComponent() {
  const {
    subCategoriaTransaccion,
    getSubCategoriaTransaccion,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
    getGastosTransacciones,
    generarPdfGastos,
    putTransaccion,
    getEntidadByID,
    user,
    updateTransaccionPago,
    postCrearPago,
    postTransaccionPago,
    deleteTransaccionPagoByTransaccion,
    deleteTransaccionPagoItem,
    getCategoriaTransaccion,
    categoriaTransaccion,
    isNavbarCollapsed,
    revertirGasto,
    parametrosGlobales,
    getParametrosGlobales,
  } = useContext(ErpContext)

  // Catálogos
  const categorias = categoriaTransaccion
  const subCategories = subCategoriaTransaccion || []
  const entidades = entidadesClientesTresLetras || []

  // Estado tabla y filtros
  const today = dayjs()

  const DEFAULT_SORT = [{ field: "fecha", sort: "desc" }]

  const [filtros, setFiltros] = useState({
    startDate: today.subtract(30, "day"),
    endDate: today,
    categoria: null,
    subcategoria: null,
    entidad: null,
  })
  const [searchTextEntidad, setSearchTextEntidad] = useState("")
  const [gastos, setGastos] = useState([])
  const [rowCount, setRowCount] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(false)
  const [totalPages, setTotalPages] = useState(0)
  const [totalMontoGastos, setTotalMontoGastos] = useState(0)
  const [sortModel, setSortModel] = useState(DEFAULT_SORT)

  // Estado confirmación reversión
  const [revertirConfirmOpen, setRevertirConfirmOpen] = useState(false)
  const [rowToRevertir, setRowToRevertir] = useState(null)

  // Estado modal y formulario
  const [openGraphModal, setOpenGraphModal] = useState(false)
  const [gestionModalState, setGestionModalState] = useState({
    open: false,
    mode: "create", // create | edit | duplicate
    initialData: null,
    actionRow: null,
    title: "Nuevo Gasto",
    onSubmit: null,
  })

  // Carga catálogos al montar
  useEffect(() => {
    getSubCategoriaTransaccion()
    getCategoriaTransaccion()
  }, [])

  // Búsqueda entidades filtro de tabla
  useEffect(() => {
    const fetchTablaEntidades = async () => {
      if (searchTextEntidad.length >= 3) {
        await getEntidadesClientesTresLetras(searchTextEntidad)
      }
    }
    fetchTablaEntidades()
  }, [searchTextEntidad])

  // Carga tabla
  const loadData = async (overrides = {}) => {
    setLoading(true)
    try {
      const nextPageRaw = overrides.page ?? page
      const nextPage = Math.max(1, Number(nextPageRaw) || 1)
      const nextSortModel = overrides.sortModel ?? sortModel
      const activeSort = Array.isArray(nextSortModel) && nextSortModel.length ? nextSortModel[0] : DEFAULT_SORT[0]
      const sortOrder = activeSort?.sort === "asc" ? "asc" : "desc"
      const sortField = activeSort?.field === "fecha" ? "fecha" : "fecha"

      const params = {
        page: nextPage, // back 1-based
        limit: 100,
        categoria: filtros.categoria?.id || null,
        idSubCategoria: filtros.subcategoria?.id || null,
        idEntidad: filtros.entidad?.id || null,
        startDate: filtros.startDate ? filtros.startDate.startOf("day").toISOString() : null,
        endDate: filtros.endDate ? filtros.endDate.endOf("day").toISOString() : null,
        sortField,
        sortOrder,
      }

      const { data, totalCount, totalPages, currentPage, totalMontoGastos: totalMonto } = await getGastosTransacciones(params)
      
      // Aplano descripciones para columnas visibles
      const filas = data.map((gasto) => {
        const fechaDate = new Date(gasto.fecha);
        return {
          ...gasto,
          fechaRaw: gasto.fecha,
          fechaTS: isNaN(fechaDate.getTime()) ? 0 : fechaDate.getTime(),
          fecha: new Intl.DateTimeFormat("es-AR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }).format(fechaDate),
          subCategoriaDesc: gasto.subCategoria?.descripcion ?? "",
          categoriaDesc: gasto.subCategoria?.categoria?.descripcion ?? "",
        };
      })

      // NO aplicar sorting local - el orden viene del servidor

      setGastos(filas)
      setRowCount(totalCount)
      setTotalPages(totalPages)
      setPage(currentPage) // mantener paginación como viene del back
      setTotalMontoGastos(Number(totalMonto || 0))
    } catch (err) {
      console.error("Error al cargar gastos:", err)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    loadData()
  }, [])

  const handleSortModelChange = (newModel) => {
    const nextSort = Array.isArray(newModel) && newModel.length ? newModel[0]?.sort : "desc"
    const normalizedSort = nextSort === "asc" ? "asc" : "desc"
    const nextModel = [{ field: "fecha", sort: normalizedSort }]
    setSortModel(nextModel)
    setPage(1)
    loadData({ page: 1, sortModel: nextModel })
  }

  const handlePageChange = (newPage) => {
    setPage(newPage)
    loadData({ page: newPage })
  }

  const handleFilterChange = (field, value) => {
    setFiltros((prev) => ({ ...prev, [field]: value }))
    setPage(0)
  }

  // Abre modal genérico
  const buscarFiltros = async () => {
    await loadData()
  }

  const buildInitialDataFromRow = async (row) => {
    const pagos = Array.isArray(row.transaccionPago) ? row.transaccionPago : []
    const pagosFiltrados = pagos.filter((tp) => tp?.eliminado !== true && tp?.pago?.eliminado !== true)
    const pagosNormalizados = pagosFiltrados
      .map((tp) => ({
        id: tp.pago?.medioDePago?.id,
        idPago: tp.idPago ?? tp.pago?.id ?? null,
        descripcion: tp.pago?.medioDePago?.descripcion || "",
        monto: tp.pago?.montoTotal || 0,
        tipoMedioDePago: tp.pago?.medioDePago?.tipoMedioDePago?.id || null,
      }))
      .filter((p) => p.id)

    const pago = pagosFiltrados[0]?.pago || {}
    const medioPago = pago.medioDePago || null
    const tipoMedioPago = medioPago?.tipoMedioDePago || null
    const esPagoMultiple = pagosNormalizados.length > 1 || tipoMedioPago?.id === 7
    const totalMediosDePagoMultiple = pagosNormalizados.reduce((total, item) => total + (Number(item.monto) || 0), 0)

    let entidadCompleta = null
    try {
      entidadCompleta = await getEntidadByID(row.idEntidad)
    } catch (err) {
      console.error("No se pudo obtener la entidad para pre-cargar edición:", err)
    }

    const entidadOption = entidadCompleta
      ? {
          label: `${entidadCompleta.descripcion} - ${entidadCompleta.apellido} - ${entidadCompleta.dniCuitCuil}`,
          descripcion: entidadCompleta.descripcion,
          apellido: entidadCompleta.apellido,
          dniCuitCuil: entidadCompleta.dniCuitCuil,
          id: entidadCompleta.id,
          idTipoEntidad: entidadCompleta.idTipoEntidad,
        }
      : row.entidad
      ? {
          label: `${row.entidad.descripcion} - ${row.entidad.apellido ?? ""} - ${row.entidad.dniCuitCuil ?? ""}`,
          descripcion: row.entidad.descripcion,
          apellido: row.entidad.apellido ?? "",
          dniCuitCuil: row.entidad.dniCuitCuil ?? "",
          id: row.idEntidad,
        }
      : null

    return {
      tipoEntidad: entidadCompleta?.idTipoEntidad || entidadOption?.idTipoEntidad || null,
      entidad: entidadOption,
      ubicacion: row.idUbicacion ? { id: row.idUbicacion } : null,
      moneda: 1,
      subcategoria: row.idCategorizacion,
      tipoMedioPago: esPagoMultiple ? 7 : tipoMedioPago,
      medioPago: esPagoMultiple ? null : medioPago,
      esPagoMultiple,
      mediosDePagoMultiple: esPagoMultiple ? pagosNormalizados : [],
      totalMediosDePagoMultiple: esPagoMultiple ? totalMediosDePagoMultiple : 0,
      monto: row.montoTotal,
      descripcion: row.descripcion,
      fecha: row.fechaRaw || row.fechaTS || null,
      archivosAdjunto: row.archivosAdjunto || null,
    }
  }

  const handleEdit = async (row) => {
    const initialData = await buildInitialDataFromRow(row)
    setGestionModalState({
      open: true,
      mode: "edit",
      initialData,
      actionRow: row,
      title: "Editar Gasto",
      onSubmit: null, // se asigna más abajo en handleGestionSubmit
    })
  }

  const handleDuplicate = async (row) => {
    const initialData = await buildInitialDataFromRow(row)
    setGestionModalState({
      open: true,
      mode: "duplicate",
      initialData,
      actionRow: null,
      title: "Duplicar Gasto",
      onSubmit: null,
    })
  }

  const handleRevertirClick = (row) => {
    setRowToRevertir(row)
    setRevertirConfirmOpen(true)
  }

  const handleRevertirConfirm = async () => {
    if (!rowToRevertir) return
    try {
      await revertirGasto(rowToRevertir.id)
      await loadData()
      setRevertirConfirmOpen(false)
      setRowToRevertir(null)
    } catch (err) {
      console.error("Error al revertir gasto:", err)
      const msg = err?.response?.data?.message || err?.response?.data?.error || "Error al revertir"
      alert(msg)
    }
  }

  const handleRevertirCancel = () => {
    setRevertirConfirmOpen(false)
    setRowToRevertir(null)
  }

  const handleExportarPDF = async () => {
    const deliveryContext = { targetWindow: reservePdfTarget() }
    try {
      setLoading(true)
      let logoBase64 = null
      try {
        const params = await getParametrosGlobales(["logoNavbarTenant"])
        logoBase64 = params?.logoNavbarTenant || null
      } catch (e) { /* ignorar */ }
      await generarPdfGastos(filtros, logoBase64, deliveryContext)
    } catch (error) {
      console.error("Error al exportar PDF:", error)
      alert("Error al generar el PDF")
    } finally {
      setLoading(false)
    }
  }

  const closeGestionModal = () =>
    setGestionModalState((prev) => ({
      ...prev,
      open: false,
      initialData: null,
      actionRow: null,
      onSubmit: null,
      mode: "create",
      title: "Nuevo Gasto",
    }))

  const handleGestionSubmit = async ({
    formData,
    mediosDePagoMultiple,
    totalMediosDePagoMultiple,
    tipoMedioPago,
    archivosAdjunto,
  }) => {
    if (gestionModalState.mode !== "edit" || !gestionModalState.actionRow) return
    const { actionRow } = gestionModalState
    await putTransaccion(
      actionRow.id,
      formData.monto,
      5,
      formData.entidad.id,
      formData.subcategoria,
      formData.descripcion,
      user.id,
      formData.ubicacion?.id || 1,
      1,
      0,
      0,
      null, // viaja
      null, // liberado
      null, // cuotas
      null, // transaccionAsociada
      null, // fecha
      archivosAdjunto !== undefined ? archivosAdjunto : undefined,
    )
    const esMultiple = (tipoMedioPago?.id || tipoMedioPago || formData.tipoMedioPago?.id) === 7
    const pagosExistentes = Array.isArray(actionRow.transaccionPago) ? actionRow.transaccionPago : []
    const teniaMultiples = pagosExistentes.length > 1

    if (esMultiple) {
      const montoTotal = Number(formData.monto) || 0
      const totalPagos = Number(totalMediosDePagoMultiple) || 0
      if (!Array.isArray(mediosDePagoMultiple) || mediosDePagoMultiple.length === 0) {
        throw new Error("Falta configurar los medios de pago múltiples.")
      }
      if (Math.abs(totalPagos - montoTotal) > 0.01) {
        throw new Error("El total de medios de pago debe ser igual al monto del gasto.")
      }

      const existentes = pagosExistentes
        .map((tp) => tp.idPago ?? tp.pago?.id)
        .filter((id) => id)
      const nuevosIds = mediosDePagoMultiple
        .map((mp) => mp.idPago)
        .filter((id) => id)

      const requiereReemplazoTotal = existentes.length !== pagosExistentes.length
      if (requiereReemplazoTotal) {
        await deleteTransaccionPagoByTransaccion(actionRow.id)
      } else {
        const eliminados = existentes.filter((id) => !nuevosIds.includes(id))
        for (const idPago of eliminados) {
          await deleteTransaccionPagoItem(actionRow.id, idPago)
        }
      }

      for (const it of mediosDePagoMultiple) {
        if (it.idPago && !requiereReemplazoTotal) continue
        const transaccionCrearPago = await postCrearPago(
          formData.moneda || 1,
          1,
          it.id,
          it.monto,
        )
        const idTransaccionCrearPago = transaccionCrearPago.data.id
        await postTransaccionPago(actionRow.id, idTransaccionCrearPago)
      }
      return
    }

    if (teniaMultiples) {
      await deleteTransaccionPagoByTransaccion(actionRow.id)
      const transaccionCrearPago = await postCrearPago(
        formData.moneda || 1,
        1,
        formData.medioPago.id,
        formData.monto,
      )
      const idTransaccionCrearPago = transaccionCrearPago.data.id
      await postTransaccionPago(actionRow.id, idTransaccionCrearPago)
      return
    }

    await updateTransaccionPago(actionRow.id, formData.medioPago.id, formData.monto)
  }

  // ==== Columnas con UI replicada de TablaVentas (solo diseño) ====
  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.3,
      minWidth: 70,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "fecha",
      headerName: "Fecha",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
          <CalendarTodayIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "montoTotal",
      headerName: "Monto",
      type: "number",
      flex: 0.8,
      minWidth: 130,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        const revertido = params.row.revertido === true
        return (
          <Chip
            icon={<AttachMoneyIcon fontSize="small" />}
            label={params.value?.toLocaleString("es-AR") || "0.00"}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: revertido ? alpha("#9e9e9e", 0.15) : alpha("#F44336", 0.1),
              color: revertido ? "#757575" : "#F44336",
              "& .MuiChip-icon": { color: revertido ? "#757575" : "#F44336" },
              display: "flex",
              textDecoration: revertido ? "line-through" : "none",
            }}
          />
        )
      },
    },
    {
      field: "entidad",
      headerName: "Proveedor",
      flex: 1.2,
      minWidth: 180,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        const label = params.row.entidad?.descripcion || ""
        return (
          <Tooltip title={label}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
              <Typography variant="body2" noWrap fontWeight={500}>
                {label}
              </Typography>
            </Box>
          </Tooltip>
        )
      },
    },
    {
      field: "descripcion",
      headerName: "Descripción",
      flex: 1.6,
      minWidth: 260,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ width: "100%", display: "flex", alignItems: "flex-start", justifyContent: "flex-start", boxSizing: "border-box", padding: "10px"}}>
          <Typography
            variant="body1"        // un toque más grande que body2
            fontWeight={600}       // resalta un poco más
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,  // hasta 2 líneas
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
              lineHeight: 1,
              color: "rgba(0,0,0,0.85)", // mejora el contraste sin cambiar la paleta
            }}
            title={params.value || ""} // tooltip nativo del browser
          >
            {params.value || ""}
          </Typography>
        </Box>
      ),
    },
    { field: "categoriaDesc", headerName: "Categoría", flex: 0.9, minWidth: 180, headerAlign: "center", sortable: false },
    { field: "subCategoriaDesc", headerName: "Subcategoría", flex: 0.9, minWidth: 180, headerAlign: "center", sortable: false },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 0.5,
      minWidth: 150,
      headerAlign: "center",
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Editar gasto">
              <IconButton
                className="colorGeneralParaIconos"
                onClick={() => handleEdit(row)}
                size="small"
                sx={{
                  bgcolor: alpha("#5b5b5f", 0.1),
                  "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Duplicar gasto">
              <IconButton
                className="colorGeneralParaIconos"
                onClick={() => handleDuplicate(row)}
                size="small"
                sx={{
                  bgcolor: alpha("#5b5b5f", 0.1),
                  "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {!row.revertido && (
              <Tooltip title="Revertir gasto">
                <IconButton
                  className="colorGeneralParaIconos"
                  onClick={() => handleRevertirClick(row)}
                  size="small"
                  sx={{
                    bgcolor: alpha("#5b5b5f", 0.1),
                    "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
                  }}
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
        </Box>
      ),
    },
  ]

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        position: "relative",
        overflow: "auto",
        padding: "16px",
      }}
    >
      <ThemeProvider theme={theme}>
        {/* Modal confirmación reversión */}
      <Dialog open={revertirConfirmOpen} onClose={handleRevertirCancel}>
        <DialogTitle>Confirmar reversión</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea revertir este gasto? Se mantendrá el registro original y se creará un ingreso que lo compensa. El ingreso aparecerá en Transacciones y en Caja.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRevertirCancel} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleRevertirConfirm} color="primary" variant="contained">
            Revertir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filtros */}
      <Paper
        className="diseñoFiltroTransaccionesVenta"
        elevation={3}
        sx={{
          p: 2,
          mb: 2,
          flexShrink: 0,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: 1.5,
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() => {
                  if (!filtros.startDate) return
                  handleFilterChange("startDate", filtros.startDate.subtract(1, "day"))
                }}
                color="primary"
                size="small"
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>

              <DatePicker
                label="Desde"
                value={filtros.startDate}
                onChange={(newValue) => handleFilterChange("startDate", newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 } } }}
                sx={{ width: 150 }}
              />

              <DatePicker
                label="Hasta"
                value={filtros.endDate}
                onChange={(newValue) => handleFilterChange("endDate", newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 } } }}
                sx={{ width: 150 }}
              />

              <IconButton
                onClick={() => {
                  if (!filtros.startDate) return
                  handleFilterChange("startDate", filtros.startDate.add(1, "day"))
                }}
                color="primary"
                size="small"
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </LocalizationProvider>

          <Autocomplete
            size="small"
            options={categorias}
            getOptionLabel={(opt) => opt.descripcion || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            value={filtros.categoria}
            onChange={(_, v) => handleFilterChange("categoria", v)}
            renderInput={(params) => <TextField {...params} label="Categoria" size="small" />}
            sx={{ width: "13%" }}
          />

          <Autocomplete
            size="small"
            options={subCategories}
            getOptionLabel={(opt) => opt.descripcion || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            value={filtros.subcategoria}
            onChange={(_, v) => handleFilterChange("subcategoria", v)}
            renderInput={(params) => <TextField {...params} label="Subcategoría" size="small" />}
            sx={{ width: "13%" }}
          />

          <Autocomplete
            size="small"
            options={entidades.map((it) => ({
              ...it,
              label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
            }))}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            value={filtros.entidad}
            onChange={(_, v) => handleFilterChange("entidad", v)}
            onInputChange={(_, txt) => setSearchTextEntidad(txt)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Buscar Entidad..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ width: "20%" }}
          />

          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() =>
                setGestionModalState({
                  open: true,
                  mode: "create",
                  initialData: null,
                  actionRow: null,
                  title: "Nuevo Gasto",
                  onSubmit: null,
                })
              }
              sx={{
                backgroundColor: "rgb(244, 177, 14)",
                textTransform: "none",
                padding: "8px 16px",
                color: "white",
                "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
              }}
            >
              Nuevo Gasto
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={buscarFiltros}
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: "#5b5b5f",
                textTransform: "none",
                padding: "8px 16px",
                color: "white",
                "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
              }}
            >
              Buscar
            </Button>

            <Tooltip title="Descargar PDF Gastos">
              <span>
                <IconButton
                  onClick={handleExportarPDF}
                  disabled={loading || gastos.length === 0}
                  sx={{
                    color: "#5b5b5f",
                    "&:hover": {
                      color: "rgb(244, 177, 14)",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(0, 0, 0, 0.26)",
                    },
                  }}
                >
                  <PictureAsPdfIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Button
              variant="contained"
              size="small"
              onClick={() => setOpenGraphModal(true)}
              sx={{
                backgroundColor: theme.palette.secondary.main,
                textTransform: "none",
                padding: "8px 16px",
                color: "white",
                "&:hover": { backgroundColor: alpha(theme.palette.secondary.main, 0.8) },
              }}
            >
              Gráficas
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabla */}
      <Paper
        sx={{
          flex: 1,
          m: 1,
          borderRadius: 3,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          position: "relative",
          minHeight: 450,
          overflow: "hidden",
        }}
      >
        <DataGrid
          key={isNavbarCollapsed ? "collapsed" : "expanded"}
          rows={gastos}
          className="tablaVentasDataGrid"
          columns={columns}
          getRowId={(row) => row.id}
          rowCount={totalPages * 100}
          pagination
          paginationMode="server"
          sortingMode="server"
          sortingOrder={["desc", "asc"]}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          getRowHeight={() => 35}
          headerHeight={35}
          sx={{
            width: "100%",
            border: 0,
            "& .MuiDataGrid-footerContainer": { display: "none !important" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#5b5b5f",
              color: "white",
              borderBottom: "2px solid #333",
              height: "45px !important",
              minHeight: "45px !important",
              maxHeight: "45px !important",
            },
            "& .MuiDataGrid-columnHeader": {
              height: "45px !important",
              minHeight: "45px !important",
              maxHeight: "45px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "white !important",
              lineHeight: "45px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
              color: "white !important",
            },
            "& .MuiDataGrid-menuIconButton": {
              color: "rgb(244, 177, 14) !important",
            },
            "& .MuiInputBase-root": {
              color: "rgb(244, 177, 14) !important",
            },
            "& .MuiTablePagination-root": {
              color: "black !important",
            },
            "& .MuiSelect-icon": {
              color: "rgb(244, 177, 14) !important",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #ccc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
            "& .MuiDataGrid-row": {
              "&:nth-of-type(even)": { backgroundColor: "#F7F7F7" },
              "&:hover": {
                backgroundColor: "#E3EAFD !important",
                transition: "background 0.3s ease",
              },
            },
            "& .row-devolucion": {
              backgroundColor: alpha("#9e9e9e !important", 0.05),
              "&:hover": { backgroundColor: alpha("#9e9e9e !important", 0.1) },
            },
            "& .MuiDataGrid-virtualScroller": { lineHeight: "none !important", height: "10px !imporant" },
            "& .MuiDataGrid-footerContainer": {
              position: "sticky",
              bottom: 0,
              backgroundColor: "#ffffff",
              borderTop: "1px solid #ddd",
              zIndex: 10,
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": { width: "2px" },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px",
            },
          }}
          components={{ Pagination: () => null }}
        />
      </Paper>

      {/* Contenedor del footer (paginación) */}
      <Box sx={{ flexShrink: 0, p: 1 }}>
        <CustomPagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={rowCount}
          montoTotalTransacciones={totalMontoGastos}
          vieneDe={"tablaGastos"}
          onPageChange={handlePageChange}
        />
      </Box>

      {/* Modal de Gráficas */}
      <Modal open={openGraphModal} onClose={() => setOpenGraphModal(false)} aria-labelledby="graph-modal-title">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "95%",
            maxWidth: 1200,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Typography id="graph-modal-title" variant="h6" gutterBottom>
            Análisis de Gastos
          </Typography>

          <GraficasGastos gastos={gastos} subCategories={subCategories} filtros={filtros} />

          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button onClick={() => setOpenGraphModal(false)} variant="contained">
              Cerrar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal de Gestionar/Editar/Duplicar Gastos */}
      <Modal
        open={gestionModalState.open}
        onClose={closeGestionModal}
        aria-labelledby="gestion-gastos-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "98%",
            maxWidth: 1400,
            minHeight: "70vh",
            maxHeight: "95vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            overflow: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: "#555",
              },
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <GestionGastosComponent
              mode={gestionModalState.mode}
              initialData={gestionModalState.initialData}
              titleOverride={gestionModalState.title}
              onClose={closeGestionModal}
              onSuccess={async () => {
                closeGestionModal()
                await loadData()
              }}
              onSubmit={gestionModalState.mode === "edit" ? handleGestionSubmit : null}
            />
          </Box>
        </Box>
      </Modal>
      </ThemeProvider>
    </div>
  )
}
